import React, { useState, useEffect } from 'react';
import FontBody1 from '../../../../../components/fonts/font-body1/font-body1';
import { FONT_COLOR, FONT_VARIANT } from '../../../../../components/fonts/font-props';

import FeaturesSection from './components/features-section';
import './store-features-display.scss';

interface IFeatureQuota {
    limit: string;
    price_per_excess_usage: number;
}

interface IFeature {
    code: number;
    name: string;
    internal_name: string;
    description: string;
    has_quota: boolean;
    is_usage_based: boolean;
    is_beta: boolean;
    quota?: IFeatureQuota;
    knowledge_base_url?: string;
}

interface IAvailableFeature {
    id: number;
    name: string;
    description: string;
    is_active: boolean;
    source: string | null;
    is_beta: boolean;
    has_quota?: boolean;
    knowledge_base_url?: string;
}

interface IStoreFeaturesResponse {
    plan: IFeature[];
    direct: IFeature[];
    all: IFeature[];
    available: IAvailableFeature[];
}

interface IStoreFeaturesDisplayProps {
    features: IStoreFeaturesResponse;
    storeId?: string | number;
    onActivateFeature?: (featureId: number) => void;
    onDeactivateFeature?: (featureId: number) => void;
    onSetQuota?: (featureId: number, quota: number) => void;
    isActivating?: boolean;
    activatingFeatureId?: number | null;
    isDeactivating?: boolean;
    deactivatingFeatureId?: number | null;
    isSettingQuota?: boolean;
    settingQuotaFeatureId?: number | null;
    expandAll?: boolean;
}

const StoreFeaturesDisplay: React.FC<IStoreFeaturesDisplayProps> = ({ 
    features, 
    storeId, 
    onActivateFeature, 
    onDeactivateFeature,
    onSetQuota,
    isActivating = false, 
    activatingFeatureId = null,
    isDeactivating = false,
    deactivatingFeatureId = null,
    isSettingQuota = false,
    settingQuotaFeatureId = null,
    expandAll = false
}) => {
    const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
        regular: true,
        beta: true,
        inactiveBeta: false
    });
    const [editingQuota, setEditingQuota] = useState<{ [key: number]: boolean }>({});
    const [quotaValues, setQuotaValues] = useState<{ [key: number]: string }>({});

    // Handle expand all functionality
    useEffect(() => {
        if (expandAll) {
            setExpandedSections({
                regular: true,
                beta: true,
                inactiveBeta: true
            });
        }
    }, [expandAll]);

    const allFeatures = features.all || [];
    const sortedFeatures = [...allFeatures].sort((a, b) => {
        // Sort non-beta features first, then beta features
        if (a.is_beta && !b.is_beta) return 1;
        if (!a.is_beta && b.is_beta) return -1;
        
        // Within each group, sort by feature code
        return a.code - b.code;
    });

    const regularFeatures = sortedFeatures.filter(feature => !feature.is_beta);
    const betaFeatures = sortedFeatures.filter(feature => feature.is_beta);

    // Get inactive features from available array
    const inactiveFeatures = features.available
        ?.filter(feature => !feature.is_active)
        .sort((a, b) => a.id - b.id) || [];

    const toggleSection = (section: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const startEditingQuota = (featureId: number, currentQuota?: string) => {
        setEditingQuota(prev => ({ ...prev, [featureId]: true }));
        // Keep the decimal value as is for editing
        const quotaValue = currentQuota || '0';
        setQuotaValues(prev => ({ ...prev, [featureId]: quotaValue }));
    };

    const cancelEditingQuota = (featureId: number) => {
        setEditingQuota(prev => ({ ...prev, [featureId]: false }));
        setQuotaValues(prev => ({ ...prev, [featureId]: '' }));
    };

    const applyQuota = (featureId: number) => {
        const quotaValue = parseFloat(quotaValues[featureId] || '0');
        if (quotaValue >= 0 && onSetQuota) {
            onSetQuota(featureId, quotaValue);
            setEditingQuota(prev => ({ ...prev, [featureId]: false }));
        }
    };

    const handleQuotaInputChange = (featureId: number, value: any) => {
        // The Input component handles number validation, just store the value
        setQuotaValues(prev => ({ ...prev, [featureId]: value?.toString() || '0' }));
    };

    const getFeatureSource = (feature: IFeature | IAvailableFeature): string => {
        if ('code' in feature) {
            const isInPlan = features.plan.some(f => f.code === feature.code);
            const isInDirect = features.direct.some(f => f.code === feature.code);
            
            if (isInPlan && isInDirect) {
                return 'Plan + Direct';
            } else if (isInDirect) {
                return 'Direct';
            } else if (isInPlan) {
                return 'Plan';
            } else {
                return 'Unknown';
            }
        }
        return '';
    };

    const getFeatureClassName = (feature: IFeature | IAvailableFeature): string => {
        if (feature.is_beta && !('is_active' in feature)) {
            return 'beta'; // Active beta features
        }
        if ('is_active' in feature && !feature.is_active) {
            return 'inactive'; // Inactive features
        }
        return ''; // Regular active features
    };

    // Check if there are any features (active or available)
    const hasActiveFeatures = allFeatures && allFeatures.length > 0;
    const hasAvailableFeatures = features.available && features.available.length > 0;
    
    if (!hasActiveFeatures && !hasAvailableFeatures) {
        return (
            <div className="store-features-display">
                <FontBody1 variant={FONT_VARIANT.DEFAULT} color={FONT_COLOR.NEUTRAL10}>
                    {isActivating ? 'Loading features...' : 'No features found for this store.'}
                </FontBody1>
            </div>
        );
    }

    return (
        <div className="store-features-display">
            <FontBody1 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY1}>
                Store Features ({allFeatures.length})
            </FontBody1>
            
            <FeaturesSection
                title="Active Features"
                features={regularFeatures}
                isExpanded={expandedSections.regular}
                onToggle={() => toggleSection('regular')}
                getFeatureSource={getFeatureSource}
                getFeatureClassName={getFeatureClassName}
                editingQuota={editingQuota}
                quotaValues={quotaValues}
                onStartEditing={startEditingQuota}
                onCancelEditing={cancelEditingQuota}
                onApplyQuota={applyQuota}
                onQuotaChange={handleQuotaInputChange}
                onSetQuota={onSetQuota}
                onActivateFeature={onActivateFeature}
                onDeactivateFeature={onDeactivateFeature}
                isActivating={isActivating}
                activatingFeatureId={activatingFeatureId}
                isDeactivating={isDeactivating}
                deactivatingFeatureId={deactivatingFeatureId}
                isSettingQuota={isSettingQuota}
                settingQuotaFeatureId={settingQuotaFeatureId}
            />

            <FeaturesSection
                title="Active Beta Features"
                features={betaFeatures}
                isExpanded={expandedSections.beta}
                onToggle={() => toggleSection('beta')}
                getFeatureSource={getFeatureSource}
                getFeatureClassName={getFeatureClassName}
                editingQuota={editingQuota}
                quotaValues={quotaValues}
                onStartEditing={startEditingQuota}
                onCancelEditing={cancelEditingQuota}
                onApplyQuota={applyQuota}
                onQuotaChange={handleQuotaInputChange}
                onSetQuota={onSetQuota}
                onActivateFeature={onActivateFeature}
                onDeactivateFeature={onDeactivateFeature}
                isActivating={isActivating}
                activatingFeatureId={activatingFeatureId}
                isDeactivating={isDeactivating}
                deactivatingFeatureId={deactivatingFeatureId}
                isSettingQuota={isSettingQuota}
                settingQuotaFeatureId={settingQuotaFeatureId}
            />

            <FeaturesSection
                title="Available Features"
                features={inactiveFeatures}
                isExpanded={expandedSections.inactiveBeta}
                onToggle={() => toggleSection('inactiveBeta')}
                getFeatureClassName={getFeatureClassName}
                editingQuota={editingQuota}
                quotaValues={quotaValues}
                onStartEditing={startEditingQuota}
                onCancelEditing={cancelEditingQuota}
                onApplyQuota={applyQuota}
                onQuotaChange={handleQuotaInputChange}
                onSetQuota={onSetQuota}
                onActivateFeature={onActivateFeature}
                onDeactivateFeature={onDeactivateFeature}
                isActivating={isActivating}
                activatingFeatureId={activatingFeatureId}
                isDeactivating={isDeactivating}
                deactivatingFeatureId={deactivatingFeatureId}
                isSettingQuota={isSettingQuota}
                settingQuotaFeatureId={settingQuotaFeatureId}
            />
        </div>
    );
};

export default StoreFeaturesDisplay;
